import React, { useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ScrollView,
  Dimensions,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { fontFamily } from "../../constants/fonts";
import { colors } from "../../constants/theme";

const B = {
  brown: "#6B4C3B",
  brownMuted: "#7D695F",
  sepia: "#9C8A80",
  creamTint: "#F3E8E2",
  border: "#E5D9D2",
  gray600: "#4b5563",
};

const housePickStyles = StyleSheet.create({
  collapsible: {
    marginTop: 4,
    marginBottom: 0,
  },
  toggle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 0,
  },
  toggleLabel: {
    flex: 1,
    color: B.brownMuted,
    fontSize: 14,
    lineHeight: 17,
    fontFamily: "AbhayaLibre_700Bold",
  },
  badgesWrap: {
    width: "100%",
    marginTop: 5,
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    alignContent: "flex-start",
    gap: 6,
    rowGap: 6,
  },
  badge: {
    alignSelf: "flex-start",
    flexGrow: 0,
    flexShrink: 0,
    maxWidth: "100%",
    borderRadius: 12,
    paddingVertical: 4,
    paddingHorizontal: 9,
    backgroundColor: B.creamTint,
    borderWidth: 1,
    borderColor: B.border,
  },
  badgeInner: {
    fontSize: 12,
    lineHeight: 15,
  },
  badgeDays: {
    color: B.brown,
    fontSize: 12,
    lineHeight: 15,
    fontFamily: "AbhayaLibre_700Bold",
  },
  badgeSep: {
    color: B.sepia,
    fontSize: 12,
    lineHeight: 15,
    fontFamily: "AbhayaLibre_400Regular",
  },
  badgeTimes: {
    color: B.brownMuted,
    fontSize: 12,
    lineHeight: 15,
    fontFamily: "AbhayaLibre_400Regular",
  },
});

const sheetStyles = StyleSheet.create({
  collapsible: {
    marginTop: 8,
    marginBottom: 0,
  },
  toggle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 0,
  },
  toggleLabel: {
    flex: 1,
    color: B.gray600,
    fontSize: 14,
    lineHeight: 17,
    fontFamily: fontFamily.abhayaBold,
  },
  badgesWrap: {
    width: "100%",
    marginTop: 6,
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    alignContent: "flex-start",
    gap: 6,
    rowGap: 6,
  },
  badge: {
    alignSelf: "flex-start",
    flexGrow: 0,
    flexShrink: 0,
    maxWidth: "100%",
    borderRadius: 12,
    paddingVertical: 4,
    paddingHorizontal: 9,
    backgroundColor: "#F3E8E2",
    borderWidth: 1,
    borderColor: "#E5D9D2",
  },
  badgeInner: {
    fontSize: 12,
    lineHeight: 15,
  },
  badgeDays: {
    color: B.brown,
    fontSize: 12,
    lineHeight: 15,
    fontFamily: fontFamily.abhayaBold,
  },
  badgeSep: {
    color: B.sepia,
    fontSize: 12,
    lineHeight: 15,
    fontFamily: fontFamily.abhaya,
  },
  badgeTimes: {
    color: B.brownMuted,
    fontSize: 12,
    lineHeight: 15,
    fontFamily: fontFamily.abhaya,
  },
});

/**
 * Collapsible hours block (House Pick cards or map bottom sheet).
 * @param {{ hours: Array<{ days?: string, times?: string }>|undefined, variant?: 'housePick' | 'sheet' }} props
 */
function renderHoursBadges(hours, styles) {
  return hours.map((entry, hi) => {
    const days = String(entry?.days ?? "").trim();
    const times = String(entry?.times ?? "").trim();
    return (
      <View key={hi} style={styles.badge} accessibilityLabel={[days, times].filter(Boolean).join(" · ") || "Hours"}>
        <Text numberOfLines={3} style={styles.badgeInner}>
          {!!days && <Text style={styles.badgeDays}>{days}</Text>}
          {!!days && !!times && <Text style={styles.badgeSep}> · </Text>}
          {!!times && <Text style={styles.badgeTimes}>{times}</Text>}
        </Text>
      </View>
    );
  });
}

function renderHoursModalRows(hours) {
  return hours.map((entry, i) => {
    const days = String(entry?.days ?? "").trim();
    const times = String(entry?.times ?? "").trim();
    const label = [days, times].filter(Boolean).join(" · ") || "Hours";
    return (
      <View
        key={i}
        style={[hoursModalBtnStyles.scheduleRow, i % 2 === 1 && hoursModalBtnStyles.scheduleRowAlt]}
        accessibilityLabel={label}
      >
        <View style={hoursModalBtnStyles.scheduleRowInner}>
          <Text style={hoursModalBtnStyles.scheduleDays}>{days || "—"}</Text>
          {!!times && <Text style={hoursModalBtnStyles.scheduleTimes}>{times}</Text>}
        </View>
      </View>
    );
  });
}

/**
 * Compact “Hours” control that opens hours in a modal.
 * @param {'tab' | 'chip'} [variant='tab'] - `tab` fills a RestaurantTabs endSlot; `chip` matches place action chips.
 */
export function OpeningHoursModalButton({ hours, openNow, variant = "tab" }) {
  const [visible, setVisible] = useState(false);
  const screenHeight = Dimensions.get("window").height;

  if (!Array.isArray(hours) || hours.length === 0) return null;

  const isChip = variant === "chip";
  // Short labels keep the action row even with Call / Website / Save.
  const label =
    openNow === true ? "Open" : openNow === false ? "Closed" : "Hours";
  const statusColor =
    openNow === true ? "#2d6a4f" : openNow === false ? "#9C8A80" : null;
  const iconColor = isChip
    ? statusColor || "#4e2916"
    : statusColor || colors.disgoBrown;
  const labelColor = isChip ? "#4e2916" : colors.disgoBrown;
  const accessibilityLabel =
    openNow === true
      ? "Hours, Open now"
      : openNow === false
        ? "Hours, Closed"
        : "Hours";

  return (
    <View style={isChip ? hoursModalBtnStyles.chipSlot : hoursModalBtnStyles.tabSlot}>
      <TouchableOpacity
        style={isChip ? hoursModalBtnStyles.chipButton : hoursModalBtnStyles.button}
        onPress={() => setVisible(true)}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
      >
        <Ionicons name="time-outline" size={18} color={iconColor} />
        <Text
          style={[
            isChip ? hoursModalBtnStyles.chipLabel : hoursModalBtnStyles.label,
            { color: labelColor },
          ]}
          numberOfLines={1}
        >
          {label}
        </Text>
      </TouchableOpacity>

      <Modal visible={visible} animationType="fade" transparent onRequestClose={() => setVisible(false)}>
        <View style={hoursModalBtnStyles.modalRoot}>
          <TouchableOpacity activeOpacity={1} onPress={() => setVisible(false)} style={hoursModalBtnStyles.modalBackdrop} />
          <View style={[hoursModalBtnStyles.modalCard, { height: screenHeight * 0.72 }]}>
            <View style={hoursModalBtnStyles.modalHeader}>
              <View style={hoursModalBtnStyles.modalHeaderIconWrap}>
                <Ionicons name="time-outline" size={22} color={colors.disgoBrown} />
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={hoursModalBtnStyles.modalTitle}>Hours</Text>
                {openNow === true || openNow === false ? (
                  <Text
                    style={{
                      marginTop: 2,
                      fontSize: 13,
                      fontFamily: fontFamily.abhayaBold,
                      color: openNow ? "#2d6a4f" : "#9C8A80",
                    }}
                  >
                    {openNow ? "Open now" : "Closed"}
                  </Text>
                ) : null}
              </View>
            </View>
            <ScrollView
              style={hoursModalBtnStyles.modalScroll}
              contentContainerStyle={hoursModalBtnStyles.modalScrollContent}
              showsVerticalScrollIndicator={true}
              bounces
            >
              <View style={hoursModalBtnStyles.scheduleList}>{renderHoursModalRows(hours)}</View>
            </ScrollView>
            <View style={hoursModalBtnStyles.modalFooter}>
              <TouchableOpacity
                style={hoursModalBtnStyles.closeButton}
                onPress={() => setVisible(false)}
                activeOpacity={0.88}
                accessibilityRole="button"
                accessibilityLabel="Close"
              >
                <Text style={hoursModalBtnStyles.closeButtonText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const hoursModalBtnStyles = StyleSheet.create({
  tabSlot: {
    flex: 1,
    minWidth: 0,
  },
  chipSlot: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  button: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: colors.tabChipMutedBg,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.tabChipBorder,
  },
  chipButton: {
    width: "100%",
    height: 40,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
    backgroundColor: "#F3E8E2",
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: "#E5D9D2",
  },
  label: {
    fontSize: 16,
    fontFamily: fontFamily.abhayaBold,
    color: colors.disgoBrown,
    fontWeight: "600",
  },
  chipLabel: {
    fontSize: 14,
    fontFamily: fontFamily.abhayaBold,
    color: "#4e2916",
  },
  modalRoot: {
    flex: 1,
    justifyContent: "center",
    paddingVertical: 24,
  },
  modalBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  modalCard: {
    backgroundColor: colors.white,
    borderRadius: 24,
    marginHorizontal: 16,
    overflow: "hidden",
    flexDirection: "column",
  },
  modalHeader: {
    alignItems: "center",
    paddingTop: 22,
    paddingBottom: 16,
    paddingHorizontal: 20,
    backgroundColor: colors.cream2,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.gray200,
  },
  modalHeaderIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.ratingChipBg,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 10,
  },
  modalTitle: {
    fontSize: 26,
    fontFamily: fontFamily.abhayaBold,
    color: colors.black,
    letterSpacing: -0.3,
  },
  modalSubtitle: {
    marginTop: 4,
    fontSize: 15,
    fontFamily: fontFamily.abhaya,
    color: colors.gray600,
  },
  modalScroll: {
    flex: 1,
  },
  modalScrollContent: {
    paddingBottom: 12,
  },
  scheduleList: {},
  scheduleRow: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.gray200,
  },
  scheduleRowAlt: {
    backgroundColor: colors.creamMuted,
  },
  scheduleRowInner: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    gap: 6,
  },
  scheduleDays: {
    fontSize: 18,
    fontFamily: fontFamily.abhayaBold,
    color: colors.disgoBrown,
    lineHeight: 24,
  },
  scheduleTimes: {
    fontSize: 17,
    fontFamily: fontFamily.abhaya,
    color: colors.gray700,
    lineHeight: 22,
  },
  modalFooter: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 20,
    backgroundColor: colors.white,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.gray200,
  },
  closeButton: {
    backgroundColor: colors.brownMuted,
    borderRadius: 12,
    paddingVertical: 15,
    alignItems: "center",
    justifyContent: "center",
  },
  closeButtonText: {
    fontSize: 18,
    fontFamily: fontFamily.abhayaBold,
    color: colors.white,
  },
});

export default function OpeningHoursCollapsible({ hours, variant = "housePick" }) {
  const [expanded, setExpanded] = useState(false);
  const styles = variant === "sheet" ? sheetStyles : housePickStyles;

  if (!Array.isArray(hours) || hours.length === 0) return null;

  return (
    <View style={styles.collapsible}>
      <TouchableOpacity
        style={styles.toggle}
        onPress={() => setExpanded((v) => !v)}
        activeOpacity={0.75}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={expanded ? "Hide hours" : "Show hours"}
        hitSlop={8}
      >
        <Ionicons name="time-outline" size={14} color={B.brownMuted} />
        <Text style={styles.toggleLabel}>Hours</Text>
        <Ionicons name={expanded ? "chevron-up" : "chevron-down"} size={20} color={B.brown} />
      </TouchableOpacity>
      {expanded ? (
        <View style={styles.badgesWrap}>{renderHoursBadges(hours, styles)}</View>
      ) : null}
    </View>
  );
}
